import * as React from 'react';
import { useField, useFormikContext } from 'formik';
import Fieldset from '../../../commonComponents/fields/Fieldset';
import { isStripeCardDocumentType, isCompletedDocument } from '../../../core/domain';
import { hasValue, spacedWordsToTitleCase } from '../../../lib/utils';
import { AdditionalNameTypes, NameChangeDocumentTypes } from '../../../core/enums';
import DocumentField from './DocumentField';
import { Component, FormSpecificationPage } from '../../../models/be/db/FormSpecification';
import Workflow from '../../../models/be/Workflow';
import { StripeCardDetails } from '../../../models/StripeCustomer';
import AdditionalName from '../../../models/be/AdditionalName';
import ErrorMessage from '../../../commonComponents/ErrorMessage';
import { requiredErrorMessage, hiddenErrorKey } from './form';
import FullName from '../../../models/be/FullName';
import { evalBoolean } from '../../../core/ast';
import './NameChangeDocuments.less';

const NameChangeDocuments = (props: Props) => {
	const [marriageDocumentFieldName, nameChangeDocumentFieldName] = props.fieldNames;
	const [, marriageFieldMeta, marriageFieldHelpers] = useField({ name: marriageDocumentFieldName });
	const [, nameChangeDocumentFieldMeta, nameChangeDocumentFieldHelpers] = useField({
		name: nameChangeDocumentFieldName
	});

	const { errors, setFieldError, values, setFieldValue } = useFormikContext<any>();
	const {
		marriageCertificateRequired,
		nameChangeDocumentRequired,
		hasMissingAdditionalName,
		misMatchedIdTypes,
		isCitizenshipCertVisibleButEmpty,
		middleNameInitialMismatch
	} = hasNameChanged(props.visibleWorkflowFields, props.formSpecificationPage, values);
	const shouldRender = marriageCertificateRequired || nameChangeDocumentRequired;

	React.useEffect(() => {
		if (!marriageCertificateRequired && values?.nameChangeDetails?.marriageCertificateDocument) {
			setFieldValue('nameChangeDetails.marriageCertificateDocument', null);
		}
		if (!nameChangeDocumentRequired && values?.nameChangeDetails?.nameChangeDocument) {
			setFieldValue('nameChangeDetails.nameChangeDocument', null);
		}
	}, [marriageCertificateRequired, nameChangeDocumentRequired, values, setFieldValue]);

	if (!marriageCertificateRequired && hasValue(marriageFieldMeta.error)) {
		marriageFieldHelpers.setError(null);
	}

	if (!nameChangeDocumentRequired && hasValue(nameChangeDocumentFieldMeta.error)) {
		nameChangeDocumentFieldHelpers.setError(null);
	}

	if (
		marriageCertificateRequired &&
		!isCompletedDocument(marriageFieldMeta.value) &&
		!hasValue(marriageFieldMeta.error)
	) {
		marriageFieldHelpers.setError(requiredErrorMessage);
	}

	if (
		nameChangeDocumentRequired &&
		!isCompletedDocument(nameChangeDocumentFieldMeta.value) &&
		!hasValue(nameChangeDocumentFieldMeta.error)
	) {
		nameChangeDocumentFieldHelpers.setError(requiredErrorMessage);
	}

	const errorMessage = 'namesDontMatch';
	const hasHiddenError =
		hasMissingAdditionalName || isCitizenshipCertVisibleButEmpty || hasValue(middleNameInitialMismatch);
	if (hasHiddenError && errors[hiddenErrorKey] !== errorMessage) {
		// Set the field after formik has finished rendering to avoid a React warning.
		setTimeout(() => {
			setFieldError(hiddenErrorKey, errorMessage);
		}, 0);
	} else if (!hasHiddenError && errors[hiddenErrorKey] === errorMessage) {
		setTimeout(() => {
			setFieldError(hiddenErrorKey, null);
		}, 0);
	}

	return (
		<>
			{shouldRender && (
				// Must set title here instead of in FormSpec section heading to solve the css issue of hiding the section legend
				// when there is no child field rendered inside this section.
				<Fieldset className="nameChangeDocument dataCaptureSection" title="Additional Name Change Documents">
					<div>
						{marriageCertificateRequired && (
							<div className="k-form-field">
								<DocumentField
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									workflowId={props.visibleWorkflowFields.id}
									candidateId={props.visibleWorkflowFields.candidateId}
									title="Marriage Certificate Image"
									documentTypes={[NameChangeDocumentTypes.MarriageCertificate]}
									fieldNames={[marriageDocumentFieldName]}
								/>
								<ErrorMessage message={marriageFieldMeta.error} />
							</div>
						)}
						{nameChangeDocumentRequired && (
							<div className="k-form-field">
								<DocumentField
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									workflowId={props.visibleWorkflowFields.id}
									candidateId={props.visibleWorkflowFields.candidateId}
									title="Name Change Document Image"
									fieldNames={[nameChangeDocumentFieldName]}
									documentTypes={[NameChangeDocumentTypes.NameChangeDocument]}
								/>
								<ErrorMessage message={nameChangeDocumentFieldMeta.error} />
							</div>
						)}
					</div>
				</Fieldset>
			)}
			{errors[hiddenErrorKey] && (
				<ErrorMessage
					message={
						<>
							{isCitizenshipCertVisibleButEmpty && (
								<p>Please provide your Citizenship Certificate details including given name and surname.</p>
							)}
							{hasMissingAdditionalName && (
								<>
									<p>
										The names on the following ID do not match with your supplied names:{' '}
										{misMatchedIdTypes
											.map((item) => `${spacedWordsToTitleCase(item.idType)}${item.name ? ` (${item.name})` : ''}`)
											.join(', ')}
										.
									</p>
									<p>
										Please check the names on your ID carefully and use the Back button to go back to the{' '}
										<a onClick={() => props.navigateToPage('names')}>Names step</a> to ensure your full name is
										correctly entered there along with any other names you have ever been known by.
									</p>
								</>
							)}
							{middleNameInitialMismatch && (
								<p>
									Your ID shows a middle name of {middleNameInitialMismatch.idMiddleName}, but on the Names step you
									only provided a middle name of {middleNameInitialMismatch.candidateInitial}. Please go back to the{' '}
									<a onClick={() => props.navigateToPage?.('names')}>Names step</a> and correct.
								</p>
							)}
						</>
					}
				/>
			)}
		</>
	);
};

export default NameChangeDocuments;

interface Props extends Component {
	visibleWorkflowFields: Workflow;
	navigateToPage?: (pageSlug: string) => void;
	stripe?: stripe.Stripe;
	formSpecificationPage?: FormSpecificationPage;
}

NameChangeDocuments.defaultProps = {
	navigateToPage: null,
	stripe: null,
	formSpecificationPage: null
};

// This Validation rule should be same as "ValidateNameChangeDocuments" in BE PhoenixCandidateDataCaptureProvider.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasNameChanged = (
	visibleWorkflowFields: Workflow,
	formSpecificationPage?: FormSpecificationPage,
	formValues?: any
) => {
	const { primaryName, additionalNames } = visibleWorkflowFields;
	const maidenAdditionalName = getMaidenAdditionalName(additionalNames);
	const previousAndAliasAdditionalNames = getPreviousAndAliasAdditionalNames(additionalNames);
	const idTypeAndNames = getIdTypeAndNames(visibleWorkflowFields, formSpecificationPage);

	let marriageCertificateRequired = false;
	let nameChangeDocumentRequired = false;
	let hasMissingAdditionalName = false;
	const misMatchedIdTypes = [] as Array<{ idType: string; name: string }>;

	idTypeAndNames.forEach((idTypeAndName) => {
		if (namesMatch(idTypeAndName, primaryName)) {
			// Do nothing.
		} else if (hasValue(maidenAdditionalName) && namesMatch(idTypeAndName, maidenAdditionalName)) {
			marriageCertificateRequired = true;
		} else if (previousAndAliasAdditionalNames.some((name) => namesMatch(idTypeAndName, name))) {
			nameChangeDocumentRequired = true;
		} else {
			hasMissingAdditionalName = true;
			const fullName =
				idTypeAndName.idType === IdTypes.BirthCertificate && hasValue(idTypeAndName.givenNames)
					? `${idTypeAndName.givenNames} ${idTypeAndName.lastName}`.trim()
					: `${idTypeAndName.firstName} ${idTypeAndName.middleNames || ''} ${idTypeAndName.lastName}`.trim();
			misMatchedIdTypes.push({ idType: idTypeAndName.idType, name: fullName });
		}
	});

	const stripeCardNames = getAllUserDocumentsExceptNameChangeDocuments(visibleWorkflowFields)
		.filter((d) => isStripeCardDocumentType(d?.documentType) && hasValue(d?.stripeCardDetails))
		.map((d) => splitCardName(d.stripeCardDetails));

	stripeCardNames.forEach((cardNames) => {
		if (hasValue(cardNames) && !doesCardNameMatch(cardNames, primaryName)) {
			if (hasValue(maidenAdditionalName) && doesCardNameMatch(cardNames, maidenAdditionalName)) {
				marriageCertificateRequired = true;
			} else if (
				previousAndAliasAdditionalNames.length > 0 &&
				doesCardNameMatchAny(cardNames, previousAndAliasAdditionalNames)
			) {
				nameChangeDocumentRequired = true;
			} else {
				hasMissingAdditionalName = true;
				const cardName = cardNames.join(' ');
				misMatchedIdTypes.push({ idType: 'bank/credit card', name: cardName });
			}
		}
	});

	if (!hasValue(maidenAdditionalName)) {
		marriageCertificateRequired = false;
	}
	if (previousAndAliasAdditionalNames.length === 0) {
		nameChangeDocumentRequired = false;
	}

	// Detect when the citizenship cert section is actually visible (using evalBoolean on the section's
	// visible condition) but the firstName field has not been provided.
	const isCitizenshipCertVisibleButEmpty =
		!hasValue(visibleWorkflowFields.citizenshipCertificateDetails?.firstName) &&
		formSpecificationPage?.sections.some(
			(section) =>
				evalBoolean(section.visible, formValues) &&
				section.components.some(
					(component) =>
						evalBoolean(component.visible, formValues) &&
						component.fieldNames?.some((fn) => fn.startsWith('citizenshipCertificateDetails'))
				)
		) === true;

	let middleNameInitialMismatch: { idMiddleName: string; candidateInitial: string } | null = null;
	idTypeAndNames.forEach((idTypeAndName) => {
		if (!middleNameInitialMismatch) {
			middleNameInitialMismatch = getMiddleNameInitialMismatch(idTypeAndName.middleNames, primaryName?.middleNames);
		}
	});

	return {
		marriageCertificateRequired,
		nameChangeDocumentRequired,
		hasMissingAdditionalName,
		misMatchedIdTypes,
		isCitizenshipCertVisibleButEmpty,
		middleNameInitialMismatch
	};
};

const getMiddleNameInitialMismatch = (
	idMiddleNames: string | null | undefined,
	candidateMiddleNames: string | null | undefined
): { idMiddleName: string; candidateInitial: string } | null => {
	if (!idMiddleNames || !candidateMiddleNames) return null;

	const idWords = idMiddleNames.split(' ').filter(Boolean);
	const candidateWords = candidateMiddleNames.split(' ').filter(Boolean);

	for (let i = 0; i < Math.min(idWords.length, candidateWords.length); i += 1) {
		const idWord = idWords[i];
		const candidateWord = candidateWords[i];

		const isInitialOnly =
			candidateWord.length === 1 && idWord.length > 1 && candidateWord.toLowerCase() === idWord[0].toLowerCase();

		if (isInitialOnly) {
			return { idMiddleName: idWord, candidateInitial: candidateWord };
		}
	}
	return null;
};

const normalizeName = (name: string | null | undefined): string => (name ?? '').replace(/[-' ]/g, '')?.toLowerCase();

// Name validation rules:
// Ignore name casing.
// BirthCertificate full name must match exactly with one of the Candidate names in Names page.
// Medicare middleNames allows initial, or
// Other middleNames rules:
// 1. Don't check middleNames if no middleNames in form ID name.
// 2. If there is middleNames in form ID name, the middleNames words should be all included in Candidate name
//		(order of the words doesn't matter for now)
const namesMatch = (idTypeAndName: IdTypeAndName, otherName: FullName) => {
	const idHasSingleName = !hasValue(idTypeAndName.lastName) && hasValue(idTypeAndName.firstName);
	const otherHasSingleName = !hasValue(otherName.lastName) && hasValue(otherName.firstName);

	if (idHasSingleName || otherHasSingleName) {
		const idFullName = normalizeName(
			`${idTypeAndName.firstName || ''} ${idTypeAndName.middleNames || ''} ${idTypeAndName.lastName || ''}`.trim()
		);
		const otherFullName = normalizeName(
			`${otherName.firstName || ''} ${otherName.middleNames || ''} ${otherName.lastName || ''}`.trim()
		);

		const idSingleName = normalizeName(idTypeAndName.firstName || idTypeAndName.lastName || '');
		const otherSingleName = normalizeName(otherName.firstName || otherName.lastName || '');

		const bothAreSingleNames = idHasSingleName && otherHasSingleName;
		const noMiddleNamesPresent = !hasValue(idTypeAndName.middleNames) && !hasValue(otherName.middleNames);

		if (bothAreSingleNames && noMiddleNamesPresent) {
			return (
				idFullName === otherFullName ||
				idSingleName === otherFullName ||
				idFullName === otherSingleName ||
				idSingleName === otherSingleName
			);
		}
		if (bothAreSingleNames) {
			return idFullName === otherFullName;
		}
		if (noMiddleNamesPresent) {
			return idFullName === otherFullName;
		}
		return false;
	}

	// People with single names will have a last name that is null or an empty string.
	const lastNamesMatch =
		(!hasValue(idTypeAndName.lastName) && !hasValue(otherName.lastName)) ||
		normalizeName(idTypeAndName.lastName) === normalizeName(otherName.lastName);

	if (idTypeAndName.idType === IdTypes.BirthCertificate) {
		const otherNameGivenNames =
			otherName.firstName + (hasValue(otherName.middleNames) ? ` ${otherName.middleNames}` : '');
		const givenNamesMatch = normalizeName(idTypeAndName.givenNames) === normalizeName(otherNameGivenNames);
		return givenNamesMatch && lastNamesMatch;
	}
	// For citizenship certificate the middle name is an explicit form entry (not a physical document),
	// so if the primary name has a middle name but the cert doesn't, that is a mismatch.
	if (
		idTypeAndName.idType === IdTypes.CitizenshipCertificate &&
		hasValue(otherName.middleNames) &&
		!hasValue(idTypeAndName.middleNames)
	) {
		return false;
	}
	const doMiddleNamesMatch = middleNamesMatch(idTypeAndName.middleNames, otherName.middleNames);
	return (
		normalizeName(idTypeAndName.firstName) === normalizeName(otherName.firstName) &&
		doMiddleNamesMatch &&
		lastNamesMatch
	);
};

function middleNamesMatch(idMiddleName: string | null, otherMiddleNames: string | null): boolean {
	if (!idMiddleName) return true;
	if (!otherMiddleNames) return false;

	const middleNameWords: string[] = otherMiddleNames.split(' ').map(normalizeName).filter(Boolean);
	const otherMiddleNameWords: string[] = idMiddleName.split(' ').map(normalizeName).filter(Boolean);

	let matchedCount = 0;
	let middleIndex = 0;
	let matchFound = true;

	otherMiddleNameWords.map((oName) => {
		matchFound = false;
		while (middleIndex < middleNameWords.length) {
			const mName: string = middleNameWords[middleIndex];

			if (
				oName === mName ||
				(oName.length === 1 && mName.startsWith(oName)) ||
				(mName.length === 1 && oName.startsWith(mName))
			) {
				matchFound = true;
				middleIndex++;
				break;
			}
			middleIndex++;
		}

		if (!matchFound) {
			return false;
		}
		matchedCount++;
		return matchedCount;
	});

	return matchedCount === otherMiddleNameWords.length;
}

const getMaidenAdditionalName = (additionalNames: AdditionalName[]) => {
	return additionalNames.find((x) => x.type === AdditionalNameTypes.Maiden);
};

const getPreviousAndAliasAdditionalNames = (additionalNames: AdditionalName[]) => {
	return additionalNames.filter((x) => x.type === AdditionalNameTypes.Alias || x.type === AdditionalNameTypes.Previous);
};

const getIdTypeAndNames = (
	visibleWorkflowFields: Workflow,
	formSpecificationPage?: FormSpecificationPage
): Set<IdTypeAndName> => {
	// Only consider fields that are visible in the current template/page, not IDV-populated fields
	const { birthDetails, passportDetails, driverLicenceDetails, medicareDetails, citizenshipCertificateDetails } =
		visibleWorkflowFields;
	const idTypeAndNames = new Set<IdTypeAndName>();

	// Check if fields are visible on the current page by looking at form specification
	const isFieldVisibleOnPage = (fieldName: string): boolean => {
		if (!formSpecificationPage) {
			// Fallback to old behavior if no form spec page provided
			const workflowTypesWithoutDriverLicenceMedicare = ['RTW', 'NZPV', 'NZRTW'];
			if (fieldName.includes('driverLicence') || fieldName.includes('medicare')) {
				return !workflowTypesWithoutDriverLicenceMedicare.includes(visibleWorkflowFields.workflowTypeCode);
			}
			return true;
		}

		// Check if any component in the current page references this field
		return formSpecificationPage.sections.some((section) =>
			section.components.some((component) => component.fieldNames?.some((fn) => fn.startsWith(fieldName)))
		);
	};

	// Only add birth certificate if birth details fields are visible on current page
	if (hasValue(birthDetails?.givenNames) && isFieldVisibleOnPage('birthDetails')) {
		idTypeAndNames.add({
			idType: IdTypes.BirthCertificate,
			givenNames: birthDetails.givenNames,
			firstName: '',
			middleNames: '',
			lastName: birthDetails.familyName ?? ''
		});
	}

	// Only add passport if passport details fields are visible on current page
	if (hasValue(passportDetails?.firstName) && isFieldVisibleOnPage('passportDetails')) {
		idTypeAndNames.add(createIdTypeAndName(IdTypes.Passport, passportDetails));
	}

	// Only add driver licence if driver licence fields are visible on current page
	if (hasValue(driverLicenceDetails?.firstName) && isFieldVisibleOnPage('driverLicenceDetails')) {
		idTypeAndNames.add(createIdTypeAndName(IdTypes.DriverLicence, driverLicenceDetails));
	}

	// Only add medicare if medicare fields are visible on current page
	if (hasValue(medicareDetails?.firstName) && isFieldVisibleOnPage('medicareDetails')) {
		idTypeAndNames.add(createIdTypeAndName(IdTypes.Medicare, medicareDetails));
	}

	// Only add citizenship certificate if it has been filled in and is on the current page.
	// The "visible but empty" case is handled separately in hasNameChanged via isCitizenshipCertVisibleButEmpty.
	if (hasValue(citizenshipCertificateDetails?.firstName) && isFieldVisibleOnPage('citizenshipCertificateDetails')) {
		idTypeAndNames.add(createIdTypeAndName(IdTypes.CitizenshipCertificate, citizenshipCertificateDetails));
	}

	return idTypeAndNames;
};

const getAllUserDocumentsExceptNameChangeDocuments = (workflow: Workflow) => {
	// TODO: Figure out a generic way to list all documents.
	return [
		workflow.passportDetails?.document,
		workflow.passportDetails?.passport,
		workflow.passportDetails?.visa,
		workflow.citizenshipCertificateDetails?.document,
		workflow.medicareDetails?.document,
		workflow.birthDetails?.document,
		workflow.driverLicenceDetails?.document,
		workflow.additionalDocuments?.documentOne,
		workflow.additionalDocuments?.documentTwo,
		workflow.additionalDocuments?.bankCardOrCreditCard,
		workflow.identityDocuments?.commencementDocument,
		workflow.identityDocuments?.primaryDocument,
		workflow.identityDocuments?.firstSecondaryDocument,
		workflow.identityDocuments?.secondSecondaryDocument,
		workflow.identityDocuments?.applicantAddressDocument,
		workflow.qualificationDetails?.degreeCertificate,
		workflow.qualificationDetails?.checkResult,
		workflow.pdfCertificate
	];
};

const splitCardName = (stripeCardDetails: StripeCardDetails) => {
	if (hasValue(stripeCardDetails?.cardName)) {
		return stripeCardDetails.cardName?.toLowerCase().split(' ');
	}
	return null;
};

const doesCardNameMatchAny = (cardNames: string[], knownNames: FullName[]) => {
	return knownNames.length > 0 && knownNames.some((knownName) => doesCardNameMatch(cardNames, knownName));
};

const doesCardNameMatch = (cardNames: string[], knownName: FullName) => {
	if (!cardNames || cardNames.length === 0 || !knownName) return false;

	const lastNames = knownName.lastName?.toLowerCase().split(' ') || [];
	const firstNames = knownName.firstName?.toLowerCase().split(' ') || [];

	return cardNames.some((cardName) => {
		const fragments = cardName?.toLowerCase().split(/[\s-]+/); // Split by space or hyphen

		// Check if any last name matches
		if (lastNames.length > 0 && lastNames.some((lastName) => fragments.includes(lastName))) {
			return true;
		}

		// If no last name, check if any first name matches
		return firstNames.some((firstName) => fragments.includes(firstName));
	});
};

// eslint-disable-next-line no-shadow
enum IdTypes {
	BirthCertificate = 'birth certificate',
	Passport = 'passport',
	DriverLicence = 'driver licence',
	Medicare = 'medicare',
	CitizenshipCertificate = 'citizenship certificate'
}

interface IdTypeAndName extends FullName {
	idType: IdTypes;
	givenNames?: string;
}

const createIdTypeAndName = (idType: IdTypes, fullName: FullName): IdTypeAndName => {
	return {
		idType,
		firstName: fullName.firstName ?? '',
		middleNames: fullName.middleNames ?? '',
		lastName: fullName.lastName ?? ''
	};
};
